"use client";

import { c as _c } from "react/compiler-runtime";
import { useDpStore } from "@/store/dpStore";
import { useState, useEffect } from "react";

/**
 * Custom hook to get the user's current location using browser geolocation API
 * Falls back to default location (Bangalore) if geolocation is unavailable or fails
 *
 * @returns {Object} { location, error, isLoading }
 * @property {Object} location - { lat: number, lng: number }
 * @property {string|null} error - Error message if geolocation fails
 * @property {boolean} isLoading - Whether location is being fetched
 */
export function useCurrentLocation() {
  const $ = _c(8);
  const geolocationSupported = typeof navigator !== "undefined" && !!navigator.geolocation;
  const {
    isMapModalOpen
  } = useDpStore();
  const [location, setLocation] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => geolocationSupported ? null : "Geolocation is not supported by your browser";
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [error, setError] = useState(t0);
  const [isLoading, setIsLoading] = useState(geolocationSupported);
  let t1;
  let t2;
  if ($[1] !== isMapModalOpen) {
    t1 = () => {
      if (!geolocationSupported) {
        return;
      }
      if (!isMapModalOpen) {
        return;
      }
      const controller = new AbortController();
      navigator.geolocation.getCurrentPosition(position => {
        if (controller.signal.aborted) {
          return;
        }
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsLoading(false);
      }, err => {
        if (controller.signal.aborted) {
          return;
        }
        let errorMessage = "Unable to retrieve your location";
        if (err && typeof err.code === "number") {
          bb30: switch (err.code) {
            case 1:
              {
                errorMessage = "Location access denied. Please enable location permissions in your browser settings.";
                break bb30;
              }
            case 2:
              {
                errorMessage = "Location information is unavailable. Please try again.";
                break bb30;
              }
            case 3:
              {
                errorMessage = "Location request timed out. Please try again.";
                break bb30;
              }
            default:
              {
                errorMessage = "Unable to retrieve your location. Please try again.";
              }
          }
        } else {
          if (err && err.message) {
            errorMessage = err.message;
          }
        }
        if (err && (err.code !== undefined || err.message)) {
          console.error("Error getting location:", {
            code: err.code,
            message: err.message,
            error: err
          });
        } else {
          console.warn("Geolocation error (empty error object), using default location");
        }
        setError(errorMessage);
        setIsLoading(false);
      }, {
        timeout: 10000
      });
      return () => {
        controller.abort();
      };
    };
    t2 = [geolocationSupported, isMapModalOpen];
    $[1] = isMapModalOpen;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== error || $[5] !== isLoading || $[6] !== location) {
    t3 = {
      location,
      error,
      isLoading
    };
    $[4] = error;
    $[5] = isLoading;
    $[6] = location;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp() {
  return {
    lat: 12.9352,
    lng: 77.6245
  };
}